import React, { useState, useEffect } from 'react';
import { X, Plus, ArrowUp, ArrowDown, Save, Pencil } from 'lucide-react';
import { ChromePicker } from 'react-color';
import { neo4jService } from '../../services/neo4jService';
import { menu_categories } from '../../misc/objs';
import './ManageHeroSlider.css';

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
    reader.readAsDataURL(file);
  });

function ManageHeroSlider() {
  const [slides, setSlides] = useState([]);
  const [products, setProducts] = useState([]);
  const [newSlide, setNewSlide] = useState({
    title: '',
    subtitle: '',
    description: '',
    category: '',  
    buttonText: '',
    productId: '',
    image: '',
    color: '#FFD700'
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingSlide, setEditingSlide] = useState(null);
  const [orderDirty, setOrderDirty] = useState(false);

  // Get all subcategories from menu_categories
  const allSubCategories = menu_categories.reduce((acc, category) => {
    return [...acc, ...category.subCategories];
  }, []).sort();

  useEffect(() => {
    // Load existing slides
    const loadSlides = async () => {
      try {
        const [fetchedSlides, fetchedProducts] = await Promise.all([
          neo4jService.getHeroSlides(),
          neo4jService.getAllProducts()
        ]);
        setSlides(fetchedSlides);
        setProducts(fetchedProducts);
        setError(null);
        setSuccess('');
      } catch (err) {
        setError('Failed to load slides');
      } finally {
        setLoading(false);
      }
    };
    loadSlides();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const selectedProduct = name === 'productId'
      ? products.find(product => product.id === value)
      : null;

    const applySlideChange = (prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'productId' && selectedProduct ? {
        category: prev.category || selectedProduct.category || '',
        image: prev.image || selectedProduct.main_image || '',
        buttonText: prev.buttonText || 'View Product'
      } : {})
    });

    if (editingSlide) {
      setEditingSlide(applySlideChange);
    } else {
      setNewSlide(applySlideChange);
    }
  };

  const handleColorChange = (color) => {
    if (editingSlide) {
      setEditingSlide(prev => ({
        ...prev,
        color: color.hex
      }));
    } else {
      setNewSlide(prev => ({
        ...prev,
        color: color.hex
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const imageDataUrl = await readFileAsDataUrl(file);
      if (editingSlide) {
        setEditingSlide(prev => ({
          ...prev,
          image: imageDataUrl
        }));
      } else {
        setNewSlide(prev => ({
          ...prev,
          image: imageDataUrl
        }));
      }
      setError('');
      setSuccess('');
    } catch (err) {
      setError(err.message);
    } finally {
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const slideWithId = {
        ...newSlide,
        order: slides.length,
        id: Date.now().toString()  // Convert to string for consistency
      };

      const addedSlide = await neo4jService.addHeroSlide(slideWithId);
      
      setSlides(prev => [...prev, addedSlide]);
      setSuccess('Slide added successfully');
      setError('');
      setNewSlide({
        title: '',
        subtitle: '',
        description: '',
        category: '',  
        buttonText: '',
        productId: '',
        image: '',
        color: '#FFD700'
      });
      setShowColorPicker(false);
    } catch (err) {
      console.error('Error adding slide:', err);
      setError('Failed to add slide');
      setSuccess('');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await neo4jService.updateHeroSlide(editingSlide.id, editingSlide);
      setSlides(prev => prev.map(slide => slide.id === editingSlide.id ? editingSlide : slide));
      setEditingSlide(null);
      setSuccess('Slide updated successfully');
      setError('');
    } catch (err) {
      setError('Failed to update slide');
      setSuccess('');
    }
  };

  const handleDelete = async (id) => {
    try {
      await neo4jService.deleteHeroSlide(id);
      setSlides(prev => prev.filter(slide => slide.id !== id));
      setOrderDirty(true);
      setSuccess('Slide deleted. Save slide order to persist the updated arrangement.');
      setError('');
    } catch (err) {
      setError('Failed to delete slide');
      setSuccess('');
    }
  };

  const moveSlide = (index, direction) => {
    const newSlides = [...slides];
    const newIndex = index + direction;
    
    if (newIndex >= 0 && newIndex < slides.length) {
      [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];
      setSlides(newSlides);
      setOrderDirty(true);
      setSuccess('');
      setError('');
    }
  };

  const handleSaveOrder = async () => {
    try {
      await neo4jService.updateHeroSlideOrder(slides);
      setOrderDirty(false);
      setSuccess('Slide order saved successfully');
      setError('');
    } catch (err) {
      setError('Failed to save slide order');
      setSuccess('');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="manage-slider">
      <h2>Manage Hero Slider</h2>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="slider-toolbar">
        <button
          type="button"
          className="save-order-button"
          onClick={handleSaveOrder}
          disabled={!orderDirty}
        >
          <Save size={18} />
          Save Slide Order
        </button>
      </div>
      
      <form onSubmit={editingSlide ? handleUpdate : handleSubmit} className="slide-form">
        <div className="form-header">
          <h3>{editingSlide ? 'Edit Slide' : 'Add Slide'}</h3>
          {editingSlide && (
            <button
              type="button"
              className="cancel-button"
              onClick={() => setEditingSlide(null)}
            >
              Cancel Editing
            </button>
          )}
        </div>

        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            name="title"
            value={editingSlide ? editingSlide.title : newSlide.title}
            onChange={handleInputChange}
            placeholder="Big choice of"
            required
          />
        </div>

        <div className="form-group">
          <label>Subtitle</label>
          <input
            type="text"
            name="subtitle"
            value={editingSlide ? editingSlide.subtitle : newSlide.subtitle}
            onChange={handleInputChange}
            placeholder="Plumbing products"
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            name="description"
            value={editingSlide ? editingSlide.description : newSlide.description}
            onChange={handleInputChange}
            placeholder="Professional tools and accessories..."
            required
          />
        </div>

        <div className="form-group">
          <label>Category</label>
          <select
            name="category"
            value={editingSlide ? editingSlide.category : newSlide.category}
            onChange={handleInputChange}
            required
          >
            <option value="">Select a category</option>
            {allSubCategories.map((subCategory, index) => (
              <option key={index} value={subCategory}>
                {subCategory}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Button Text</label>
          <input
            type="text"
            name="buttonText"
            value={editingSlide ? editingSlide.buttonText : newSlide.buttonText}
            onChange={handleInputChange}
            placeholder="Shop Now"
            required
          />
        </div>

        <div className="form-group">
          <label>Linked Product</label>
          <select
            name="productId"
            value={editingSlide ? editingSlide.productId || '' : newSlide.productId}
            onChange={handleInputChange}
          >
            <option value="">No linked product</option>
            {products.map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}{product.brand ? ` - ${product.brand}` : ''}
              </option>
            ))}
          </select>
          <span className="field-note">If selected, the slide can reuse that product data, but the hero button still opens the category page.</span>
        </div>

        <div className="form-group">
          <label>Image URL</label>
          <input
            type="url"
            name="image"
            value={editingSlide ? editingSlide.image : newSlide.image}
            onChange={handleInputChange}
            placeholder="https://example.com/image.jpg"
            required
          />
          <label className="upload-button">
            Upload Image
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="file-input"
            />
          </label>
          {(editingSlide ? editingSlide.image : newSlide.image) && (
            <img
              src={editingSlide ? editingSlide.image : newSlide.image}
              alt="Hero preview"
              className="form-image-preview"
            />
          )}
        </div>

        <div className="form-group color-picker-group">
          <label>Color</label>
          <div className="color-preview" onClick={() => setShowColorPicker(!showColorPicker)}>
            <div className="color-swatch" style={{ backgroundColor: editingSlide ? editingSlide.color : newSlide.color }} />
            <span>{editingSlide ? editingSlide.color : newSlide.color}</span>
          </div>
          {showColorPicker && (
            <div className="color-picker-popover">
              <div className="color-picker-cover" onClick={() => setShowColorPicker(false)} />
              <ChromePicker
                color={editingSlide ? editingSlide.color : newSlide.color}
                onChange={handleColorChange}
                disableAlpha={true}
              />
            </div>
          )}
        </div>

        <button type="submit" className="submit-button">
          {editingSlide ? 'Save Changes' : 'Add Slide'}
          {editingSlide ? <X size={20} /> : <Plus size={20} />}
        </button>
      </form>

      <div className="slides-list">
        {slides.map((slide, index) => (
          <div key={slide.id} className="slide-item">
            <img src={slide.image} alt={slide.title} className="slide-preview" />
            <div className="slide-content">
              <div className="slide-card-header">
                <h3>{slide.title}</h3>
                <div className="slide-color-chip">
                  <span
                    className="slide-color-swatch"
                    style={{ backgroundColor: slide.color || '#FFD700' }}
                  />
                  <span>{slide.color || '#FFD700'}</span>
                </div>
              </div>
              <p>{slide.subtitle}</p>
              <div className="slide-actions">
                <button
                  type="button"
                  onClick={() => moveSlide(index, -1)}
                  disabled={index === 0}
                  className="move-button"
                >
                  <ArrowUp size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => moveSlide(index, 1)}
                  disabled={index === slides.length - 1}
                  className="move-button"
                >
                  <ArrowDown size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => setEditingSlide(slide)}
                  className="edit-button"
                  title="Edit slide"
                >
                  <Pencil size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(slide.id)}
                  className="delete-button"
                  title="Delete slide"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ManageHeroSlider;
